import { router } from "@inertiajs/react"
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"

/**
 * OKS-1037 (PO walk D1) — a confirm-address refusal whose dialog is GONE.
 *
 * The race: the creator confirms (or types) their address while the brand's
 * dialog is open. The server correctly refuses the brand (`already_confirmed`
 * / `stale`, nothing written, 302 + errors bag) — but the creator's save gave
 * the row a snapshot, so the reloaded props move it Waiting → Ready. The
 * Waiting row unmounts, its dialog with it, and the refusal had nowhere to
 * render: the operator saw the row jump sections with no word of why.
 *
 * # Mechanism
 *
 * The dialog's `<Form onError>` hands the base refusal to this provider, which
 * lives on the page and so outlives the row. The provider renders it ONLY
 * while no confirm-address dialog is open:
 *
 *   dialog still open  → the dialog shows it (OKS-1006), the page stays silent
 *   dialog unmounted   → the page shows it, once, with « Fermer »
 *
 * A new submit, a success, or closing a dialog clears it.
 *
 * ⚠️ NOT read from `usePage().props.errors`. The page error bag is banned as a
 * render source (`docs/frontend/inertia-error-bag.md`): a manual `only:`
 * reload wipes it and browser Back replays a stale copy from history. The
 * message here is plain React state captured from the visit's own callback, so
 * neither hazard applies — and another door's `errors.base` (remind,
 * mark-shipped) can never be shown as a confirm-address refusal.
 *
 * Rejected: keying the dialog at page level so it survives the section move.
 * It lifts every trigger's open state into the page and still has to be
 * re-parented across two sections; this is additive and leaves the dialog
 * where the row renders it.
 */
export interface RefusalApi {
  refused: (message: string) => void
  settled: () => void
  registerOpen: () => () => void
}

export const CONFIRM_ADDRESS_RELOAD_ONLY = [
  "ready_parcels",
  "waiting",
  "counts",
]

/**
 * OKS-1040 — the mechanism above, as a FACTORY, so a second door gets its own
 * instance instead of sharing this one. Each instance has its own context, so
 * a mark-shipped refusal can never surface as a confirm-address one (and vice
 * versa) — the scoping the note above insists on. `ship-refusal.tsx` is the
 * second instance (OKS-1040 PO re-walk R1).
 */
export function createOrphanRefusal({
  testIdPrefix,
  closeKey,
}: {
  /** e.g. `shipments-confirm-address` → `…-orphan-refusal` / `…-orphan-close`. */
  testIdPrefix: string
  /** i18n key of the « Fermer » button. */
  closeKey: string
}) {
  const RefusalContext = createContext<RefusalApi | null>(null)

  /** `null` outside the Shipments page — the dialog then just skips the wiring. */
  function useRefusal(): RefusalApi | null {
    return useContext(RefusalContext)
  }

  function Provider({ children }: { children: ReactNode }) {
    const { t } = useTranslation()
    const [message, setMessage] = useState<string | null>(null)
    const [openCount, setOpenCount] = useState(0)
    const alertRef = useRef<HTMLDivElement>(null)

    const api = useMemo<RefusalApi>(
      () => ({
        refused: (next) => setMessage(next),
        settled: () => setMessage(null),
        registerOpen: () => {
          setOpenCount((count) => count + 1)
          return () => setOpenCount((count) => count - 1)
        },
      }),
      [],
    )

    const orphaned = message !== null && openCount === 0

    // The operator's focus was inside the dialog that just unmounted.
    useEffect(() => {
      if (orphaned) alertRef.current?.focus()
    }, [orphaned])

    return (
      <RefusalContext.Provider value={api}>
        {orphaned && (
          <Alert
            ref={alertRef}
            variant="destructive"
            role="alert"
            tabIndex={-1}
            data-testid={`${testIdPrefix}-orphan-refusal`}
          >
            <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
              <span>{message}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="max-sm:min-h-11"
                onClick={() => {
                  setMessage(null)
                  router.reload({ only: CONFIRM_ADDRESS_RELOAD_ONLY })
                }}
                data-testid={`${testIdPrefix}-orphan-close`}
              >
                {t(closeKey)}
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {children}
      </RefusalContext.Provider>
    )
  }

  return { Provider, useRefusal }
}

// inertia-i18n-use shipments.confirm_address.close
const confirmAddress = createOrphanRefusal({
  testIdPrefix: "shipments-confirm-address",
  closeKey: "shipments.confirm_address.close",
})

export const ConfirmAddressRefusalProvider = confirmAddress.Provider
export const useConfirmAddressRefusal = confirmAddress.useRefusal
